import { useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "../AppContextProvider";
import dealsService from "../services/dealsService";
import { fanOutChildren } from "../pages/dealDetail/tabs/dealWideChildren";
import { dealHeaderSummary } from "../utils/dealHeaderSummary";

const rowsOf = (resp) => {
  const rows = resp?.data?.data ?? resp?.data;
  return Array.isArray(rows) ? rows : [];
};

const NO_ROWS = { dealEntities: [], leaseDeeds: [], tracts: [] };

/**
 * The deal header's Seller / County / State / Effective Date, derived from the
 * deal's child records (see utils/dealHeaderSummary for the rules).
 *
 * Uses the same endpoints the Entities and Lease/Deeds tabs call, with the
 * caller's own token, so the header can never show a value those tabs would
 * not: row scoping for external brokers is applied server-side on each call.
 * Tracts are fanned out one call per lease/deed, as the tab's "Whole deal"
 * view does (there is no per-deal tract endpoint).
 *
 * Re-fetches whenever the page hands it a new `deal` object — the page
 * re-fetches the deal row after lease/deed and tract writes
 * (`onDealDerivedChange`), so those edits refresh the header too.
 *
 * Each source fails independently: a failed call leaves its box at "—"
 * rather than blanking the whole card.
 *
 * @param {object|null} deal — the loaded deal row (needs `id`, `deal_type`,
 *   `assignment_effective_date`)
 * @param {Record<string|number, string>} [entityNames] — entity id → name,
 *   e.g. `useLookupLabels().labelByLookup.entities`, which the header card
 *   already loads (no second fetch of the entity list here)
 * @returns {{ seller: string|null, county: string|null, state: string|null,
 *   effectiveDate: string|null }}
 */
const useDealHeaderSummary = (deal, entityNames) => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
  const dealId = deal?.id ?? null;
  const [rows, setRows] = useState(NO_ROWS);

  useEffect(() => {
    if (dealId == null || !userOid) return undefined;
    let cancelled = false;
    // Never show the previous deal's values while this one loads.
    setRows(NO_ROWS);
    const load = async () => {
      let t = token;
      if (!t || (isTokenExpired && isTokenExpired(t))) t = await getToken();
      const args = { token: t, userOid };
      const [entResp, ldResp] = await Promise.allSettled([
        dealsService.listDealEntities({ ...args, dealId }),
        dealsService.listLeaseDeeds({ ...args, dealId }),
      ]);
      const val = (s) => (s.status === "fulfilled" ? rowsOf(s.value) : []);
      const leaseDeeds = val(ldResp);
      const tracts = await fanOutChildren({
        masters: leaseDeeds,
        fetchChild: (ld) =>
          dealsService.listLeaseDeedTracts({ ...args, leaseDeedId: ld.id }),
        labelOf: () => "",
      });
      if (!cancelled) setRows({ dealEntities: val(entResp), leaseDeeds, tracts });
    };
    load().catch(() => {
      // Token refresh failed: leave the boxes at "—"; the page itself shows
      // the auth error.
    });
    return () => {
      cancelled = true;
    };
    // Auth context is closed over. `deal` (identity) is the refresh signal:
    // the page replaces it after an Overview save and after lease/deed or
    // tract writes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal, userOid]);

  return useMemo(
    () => dealHeaderSummary({ deal, entityNames, ...rows }),
    [deal, entityNames, rows]
  );
};

export default useDealHeaderSummary;
